import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

/**
 * The sidebar is the frame (docs/ui/frame.md §4): groups in funnel order, labels in the AM's
 * words, routes unchanged. One sidebar for every employee (VT, 2026-09-04): an Executive, an
 * Administrator or a Supervisor sees everything an Account Manager sees. The old split (admin-only
 * accounts got Clients plus the Admin group) is gone with its guard; role gates come later, in one
 * pass across Juno, and until then a server gate answers on screen ("You don't have access to
 * this."), never as a missing item. Onboarding is not an item: the wizard is entered from a lead,
 * a place, or New client on Clients. Revenue appears only once built; Affiliates shows an
 * under-construction page by decision. Admin is Employees alone since 2026-09-04: a client's
 * logins are per-client work and belong on the client record, not in nav (client-360. Since 2026-09-07 Admin also holds Data,
 * navigator's operator page (task-admin-data-page.md): import, reindex, the market's numbers, ADMIN only on the server.md §7 row 6).
 */
@Injectable({
  providedIn: 'root'
})
export class SidebarService {
  getSideBarItems(): Observable<SideBarItem[]> {
    // fresh copies: the component mutates `selected`
    return of(items.map(item => ({...item})))
  }
}

export type SidebarGroup = 'source' | 'clients' | 'finance' | 'influencers' | 'catalog' | 'admin'

export const SIDEBAR_GROUP_LABELS: Record<SidebarGroup, string> = {
  source: 'Source',
  clients: 'Clients',
  finance: 'Finance',
  influencers: 'Influencers',
  catalog: 'Catalog',
  admin: 'Admin',
}

export interface SideBarItem {
  id: string
  name: string
  path: string
  googleIcon: string
  selected: boolean
  /** items render under a divider and the group's label at each change of group */
  group: SidebarGroup
}

const item = (id: string, name: string, path: string, googleIcon: string, group: SidebarGroup): SideBarItem =>
  ({ id, name, path, googleIcon, group, selected: false })

const salons = item('salons', 'Salons', '/salons', 'storefront', 'source')
const leads = item('leads', 'Leads', '/leads', 'contacts', 'source')
// decided, not built: the item leads to an honest page rather than being absent (VT, 2026-09-03)
const affiliates = item('affiliates', 'Affiliates', '/affiliates', 'handshake', 'source')
const clients = item('clients', 'Clients', '/businesses', 'work', 'clients')
const subscriptions = item('subscriptions', 'Subscriptions', '/subscription-management', 'card_membership', 'finance')
const invoices = item('invoices', 'Invoices', '/invoices', 'receipt_long', 'finance')
const influencers = item('influencers', 'Influencers', '/influencers', 'groups', 'influencers')
const payouts = item('payouts', 'Payouts', '/payouts', 'payments', 'influencers')
const products = item('products', 'Products', '/products', 'inventory_2', 'catalog')
const employees = item('employees', 'Employees', '/admin/employees', 'badge', 'admin')
// navigator's operator page: import, reindex, the market's numbers (task-admin-data-page.md)
const data = item('data', 'Data', '/admin/data', 'database', 'admin')

const items: SideBarItem[] = [salons, leads, affiliates, clients, subscriptions, invoices, influencers, payouts, products, employees, data]
